import { createContext, useContext, useEffect, useState } from "react";

import { logStorageBlocked } from "@/lib/observability/sentry";

type Theme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window !== "undefined") {
      try {
        const stored = localStorage.getItem("evooluir-theme");
        if (stored === "dark" || stored === "light") return stored;
      } catch (e) {
        // storage blocked by browser policy (EVOOLUIR-ADMIN-REACT-C)
        logStorageBlocked("theme.get", e);
      }
    }
    return "light"; // Default to light theme
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    try {
      localStorage.setItem("evooluir-theme", theme);
    } catch (e) {
      // storage blocked by browser policy
      logStorageBlocked("theme.set", e);
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
